import React, { memo, useEffect, useMemo } from 'react';

import { FormHelperText } from '@mui/material';
import _ from 'lodash';
import PropTypes from 'prop-types';

import {
  argyleDuplicatePolicyOptions,
  finicityDuplicatePolicyOptions,
  LoanEditorActionsEnum,
  mortgageInsuranceLoanActionTypes,
  runLoanCalculationsSaveModeOptions,
} from 'constants/loanActionEditor';
import { mismoDocumentTypeOptions, ucdTypeOptions } from 'constants/options';

import useFormContext from './hooks/useFormContext';
import LoanActionAppraisalQcServiceRow from './LoanActionAppraisalQcServiceRow';
import LoanActionAppraisalServiceRow from './LoanActionAppraisalServiceRow';
import LoanActionAssignAeRow from './LoanActionAssignAeRow';
import LoanActionClosingPortalAccessRow from './LoanActionClosingPortalAccessRow';
import LoanActionConditionRow from './LoanActionConditionRow';
import LoanActionConstantFieldRow from './LoanActionConstantFieldRow';
import LoanActionCriteriaRow from './LoanActionCriteriaRow';
import LoanActionDecideCertificationRow from './LoanActionDecideCertificationRow';
import LoanActionDuplicatePolicyRow from './LoanActionDuplicatePolicyRow';
import LoanActionEmailRow from './LoanActionEmailRow';
import LoanActionEnterCustomFieldsRow from './LoanActionEnterCustomFieldsRow';
import LoanActionExecuteAiProcessRow from './LoanActionExecuteAiProcessRow';
import LoanActionExportLoanPackageRow from './LoanActionExportLoanPackageRow';
import LoanActionFhaServiceRow from './LoanActionFhaServiceRow';
import LoanActionFloodServiceRow from './LoanActionFloodServiceRow';
import LoanActionHalcyonAddFormsRow from './LoanActionHalcyonAddFormsRow';
import LoanActionIframeRow from './LoanActionIframeRow';
import LoanActionImpoundRow from './LoanActionImpoundRow';
import LoanActionIntegrationRow from './LoanActionIntegrationRow';
import LoanActionFormItem from './LoanActionItem';
import LoanActionMailDocumentPackageRow from './LoanActionMailDocumentPackageRow';
import LoanActionMersPreviewRow from './LoanActionMersPreviewRow';
import LoanActionMersRow from './LoanActionMersRow';
import { getMersRowConfig } from './LoanActionMersRow/LoanActionMersRow.config';
import LoanActionMismoDocumentRow from './LoanActionMismoDocumentRow';
import LoanActionMortgageInsuranceRow from './LoanActionMortgageInsuranceRow';
import LoanActionPdfRow from './LoanActionPdfRow';
import LoanActionReadinessStatusRow from './LoanActionReadinessStatusRow';
import LoanActionReportRow from './LoanActionReportRow';
import LoanActionFormRow from './LoanActionRow';
import LoanActionRows from './LoanActionRows';
import LoanActionRunAiProcessRow from './LoanActionRunAiProcessRow';
import LoanActionRunLoanCalculationsRow from './LoanActionRunLoanCalculationsRow';
import LoanActionSavePendingChangesPermissionRow from './LoanActionSavePendingChangesPermissionRow';
import LoanActionSendAiProcessResultsRow from './LoanActionSendAiProcessResultsRow';
import {
  checkIsMultipleActionItemEntityAllowed,
  deleteDuplicateActions,
  getIsActionTypeOptionDisabled,
} from './LoanActionsGroup.utils';
import LoanActionSnapdocsRow from './LoanActionSnapdocsRow';
import { snapdocsFieldsByActionType } from './LoanActionSnapdocsRow/LoanActionSnapdocsRow.constants';
import LoanActionSubmitUcdRow from './LoanActionSubmitUcdRow';
import LoanActionTagRow from './LoanActionTagRow';
import LoanActionTaskRow from './LoanActionTaskRow';
import LoanActionUpdateFloodDeterminationRow from './LoanActionUpdateFloodDeterminationRow';
import LoanActionUploadFormDeliveryRow from './LoanActionUploadFormDeliveryRow';
import LoanActionVariableFieldRow from './LoanActionVariableFieldRow';
import LoanActionVaServiceRow from './LoanActionVaServiceRow';

const emptyOptions = [];
const getOptionFieldValue = option => option.field?.value;
const getOptionValue = option => option?.value;

const LoanActionsGroup = ({
  isLoading,
  isEditAllowed,
  groupName,
  isAutomatedActions,
  ...options
}) => {
  const { form, setInForm } = useFormContext();

  const { isTimedSequence } = form;

  useEffect(() => {
    if (isTimedSequence) {
      return;
    }

    setInForm('actions', deleteDuplicateActions(form.actions));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isTimedSequence, setInForm]);

  const unusedTagOptions = useMemo(() => {
    const usedTags = form.actions.flatMap(({ tags }) => tags?.map(tag => tag?.value));

    return options.tagsOptions.filter(({ value }) => !usedTags.includes(value));
  }, [form.actions, options.tagsOptions]);

  const unusedReportsOptions = useMemo(() => {
    const usedReports = form.actions.flatMap(({ reports }) =>
      reports?.map(({ report }) => report?.value),
    );

    return options.reportsOptions.filter(({ value }) => !usedReports.includes(value));
  }, [form.actions, options.reportsOptions]);

  const getOptionDisabled = option =>
    getIsActionTypeOptionDisabled({ option, actions: form.actions });

  return form.actions.map((action, actionIndex) => {
    const itemType = _.get(form, `actions[${actionIndex}].itemType`)?.value;
    const isMultipleEntityAllowed = checkIsMultipleActionItemEntityAllowed({ action, options });
    const mersRowConfig = getMersRowConfig(itemType, isAutomatedActions);

    return (
      <LoanActionFormItem
        action={action}
        actionIndex={actionIndex}
        getOptionDisabled={isAutomatedActions ? undefined : getOptionDisabled}
        isDisabled={isLoading}
        isEditAllowed={isEditAllowed}
        isMultipleEntityAllowed={isMultipleEntityAllowed}
        key={actionIndex}
        label={groupName}
        options={options.groupOptions}
        rootPath="actions"
      >
        {itemType === LoanEditorActionsEnum.SET_FIELD_STATIC && (
          <LoanActionRows
            component={LoanActionConstantFieldRow}
            isDisabled={isLoading}
            isEditAllowed={isEditAllowed}
            items={action.fields}
            optionValueGetter={getOptionFieldValue}
            options={options.fieldsOptions}
            rootPath={`actions[${actionIndex}].fields`}
          />
        )}

        {itemType === LoanEditorActionsEnum.SET_FIELD_DYNAMIC && (
          <LoanActionRows
            component={LoanActionVariableFieldRow}
            isDisabled={isLoading}
            isEditAllowed={isEditAllowed}
            items={action.variables}
            optionValueGetter={getOptionFieldValue}
            options={options.extendedFieldsOptions}
            rootPath={`actions[${actionIndex}].variables`}
          />
        )}

        {itemType === LoanEditorActionsEnum.ENTER_CUSTOM_FIELDS && (
          <LoanActionRows
            component={LoanActionEnterCustomFieldsRow}
            isDisabled={isLoading}
            isEditAllowed={isEditAllowed}
            items={action.enterCustomFields}
            optionValueGetter={getOptionValue}
            options={options.customFieldDefinitionOptions || emptyOptions}
            rootPath={`actions[${actionIndex}].enterCustomFields`}
          />
        )}

        {(itemType === LoanEditorActionsEnum.ADD_TAG ||
          itemType === LoanEditorActionsEnum.REMOVE_TAG) &&
          action.tags.map((__, tagIndex, selectedTags) => (
            <LoanActionTagRow
              isDisabled={isLoading}
              isEditAllowed={isEditAllowed}
              isSingleItem={selectedTags.length === 1}
              key={tagIndex}
              rootPath={`actions[${actionIndex}].tags`}
              tagIndex={tagIndex}
              tagsOptions={unusedTagOptions}
            />
          ))}

        {(itemType === LoanEditorActionsEnum.SEND_FORM ||
          itemType === LoanEditorActionsEnum.GENERATE_AND_SAVE_FORM ||
          itemType === LoanEditorActionsEnum.GENERATE_FORM) && (
          <LoanActionRows
            component={LoanActionFormRow}
            isDisabled={isLoading}
            isEditAllowed={isEditAllowed}
            itemType={itemType}
            items={action.forms}
            label="Form"
            optionValueGetter={getOptionValue}
            options={options.formsOptions}
            rootPath={`actions[${actionIndex}].forms`}
          />
        )}

        {itemType === LoanEditorActionsEnum.GENERATE_PDF && (
          <LoanActionRows
            component={LoanActionPdfRow}
            formBundlesOptions={options.formBundlesOptions}
            formsOptions={options.formsOptions}
            isDisabled={isLoading}
            isEditAllowed={isEditAllowed}
            itemType={itemType}
            items={action.pdf}
            label="Pdf"
            optionValueGetter={getOptionValue}
            // eslint-disable-next-line react-perf/jsx-no-new-array-as-prop
            options={[]}
            rootPath={`actions[${actionIndex}].pdf`}
          />
        )}

        {itemType === LoanEditorActionsEnum.CREATE_DOCUMENT_FROM_SOURCE && (
          <LoanActionRows
            withDocumentTypeSelection
            component={LoanActionPdfRow}
            documentTypesOptions={options.documentTypesOptions}
            formBundlesOptions={options.formBundlesOptions}
            formsOptions={options.formsOptions}
            isDisabled={isLoading}
            isEditAllowed={isEditAllowed}
            itemType={itemType}
            items={action.createDocumentFromSource}
            label="Document"
            optionValueGetter={getOptionValue}
            options={emptyOptions}
            rootPath={`actions[${actionIndex}].createDocumentFromSource`}
          />
        )}

        {itemType === LoanEditorActionsEnum.SET_FORM_BUNDLE_READINESS_STATUS && (
          <LoanActionRows
            component={LoanActionReadinessStatusRow}
            formBundlesOptions={options.formBundlesOptions}
            isDisabled={isLoading}
            isEditAllowed={isEditAllowed}
            items={action.setFormBundleReadinessStatus}
            optionValueGetter={getOptionValue}
            options={emptyOptions}
            rootPath={`actions[${actionIndex}].setFormBundleReadinessStatus`}
          />
        )}

        {itemType === LoanEditorActionsEnum.SEND_FORM_BUNDLE && (
          <LoanActionRows
            component={LoanActionFormRow}
            isDisabled={isLoading}
            isEditAllowed={isEditAllowed}
            itemType={itemType}
            items={action.formBundles}
            label="Form Bundle"
            optionValueGetter={getOptionValue}
            options={options.formBundlesOptions}
            rootPath={`actions[${actionIndex}].formBundles`}
            withPreview={!isAutomatedActions}
          />
        )}

        {itemType === LoanEditorActionsEnum.MAIL_DOCUMENT_PACKAGE && (
          <LoanActionRows
            component={LoanActionMailDocumentPackageRow}
            formBundlesOptions={options.formBundlesOptions}
            isDisabled={isLoading}
            isEditAllowed={isEditAllowed}
            items={action.mailDocumentPackage}
            optionValueGetter={getOptionValue}
            options={emptyOptions}
            rootPath={`actions[${actionIndex}].mailDocumentPackage`}
          />
        )}

        {itemType === LoanEditorActionsEnum.UPLOAD_FORM_DELIVERY && (
          <LoanActionRows
            component={LoanActionUploadFormDeliveryRow}
            formBundlesOptions={options.formBundlesOptions}
            isDisabled={isLoading}
            isEditAllowed={isEditAllowed}
            items={action.uploadFormDelivery}
            optionValueGetter={getOptionValue}
            options={emptyOptions}
            rootPath={`actions[${actionIndex}].uploadFormDelivery`}
          />
        )}

        {(itemType === LoanEditorActionsEnum.ORDER_UPDATE_REPORT_AUTO ||
          itemType === LoanEditorActionsEnum.ORDER_UPDATE_REPORT_MANUAL) && (
          <LoanActionRows
            component={LoanActionReportRow}
            creditReportActionSubtypeOptions={options.creditReportActionSubtypeOptions}
            floodProductOptions={options.floodProductOptions}
            isAutomatedActions={itemType === LoanEditorActionsEnum.ORDER_UPDATE_REPORT_AUTO}
            isDisabled={isLoading}
            isEditAllowed={isEditAllowed}
            items={action.reports}
            label="Report"
            optionValueGetter={getOptionValue}
            options={unusedReportsOptions}
            rootPath={`actions[${actionIndex}].reports`}
          />
        )}

        {itemType === LoanEditorActionsEnum.GENERATE_CHECKLIST && (
          <LoanActionRows
            component={LoanActionFormRow}
            isDisabled={isLoading}
            isEditAllowed={isEditAllowed}
            items={action.checklists}
            label="Checklist"
            optionValueGetter={getOptionValue}
            options={options.checklistsOptions}
            rootPath={`actions[${actionIndex}].checklists`}
          />
        )}

        {itemType === LoanEditorActionsEnum.RUN_AUS && (
          <LoanActionRows
            component={LoanActionFormRow}
            isDisabled={isLoading}
            isEditAllowed={isEditAllowed}
            items={action.runAusSubmit}
            label="AUS"
            optionValueGetter={getOptionValue}
            options={options.runAusOptions}
            rootPath={`actions[${actionIndex}].runAusSubmit`}
          />
        )}

        {itemType === LoanEditorActionsEnum.SELECT_AUS_PROVIDER && (
          <LoanActionRows
            component={LoanActionFormRow}
            isDisabled={isLoading}
            isEditAllowed={isEditAllowed}
            items={action.ausProvider}
            label="Provider"
            optionValueGetter={getOptionValue}
            options={options.ausProviderOptions}
            rootPath={`actions[${actionIndex}].ausProvider`}
          />
        )}

        {itemType === LoanEditorActionsEnum.RESET_AUS_FINDINGS && isAutomatedActions && (
          <LoanActionRows
            component={LoanActionFormRow}
            isDisabled={isLoading}
            isEditAllowed={isEditAllowed}
            items={action.resetAusProvider}
            label="Provider"
            optionValueGetter={getOptionValue}
            options={options.resetAusProviderOptions}
            rootPath={`actions[${actionIndex}].resetAusProvider`}
          />
        )}

        {itemType === LoanEditorActionsEnum.OPEN_LOAN_EDITOR_FORM && (
          <LoanActionRows
            component={LoanActionFormRow}
            isDisabled={isLoading}
            isEditAllowed={isEditAllowed}
            items={action.loanEditorCustomForm}
            label="Form"
            optionValueGetter={getOptionValue}
            options={options.loanEditorCustomFormsOptions}
            rootPath={`actions[${actionIndex}].loanEditorCustomForm`}
          />
        )}

        {itemType === LoanEditorActionsEnum.OPEN_URL_IN_IFRAME && (
          <LoanActionIframeRow
            index={0}
            isDisabled={isLoading}
            isEditAllowed={isEditAllowed}
            rootPath={`actions[${actionIndex}].openUrlInIframe`}
          />
        )}

        {(itemType === LoanEditorActionsEnum.ASSIGN_LOAN_ROLE ||
          itemType === LoanEditorActionsEnum.UNASSIGN_LOAN_ROLE) && (
          <LoanActionRows
            component={LoanActionCriteriaRow}
            isAutomatedActions={isAutomatedActions}
            isDisabled={isLoading}
            isEditAllowed={isEditAllowed}
            itemType={itemType}
            items={action.loanRole}
            label="Loan Role"
            optionValueGetter={getOptionValue}
            options={options.loanRoleOptions}
            rootPath={`actions[${actionIndex}].loanRole`}
          />
        )}

        {(itemType === LoanEditorActionsEnum.ASSIGN_CHAT_MEMBERS ||
          itemType === LoanEditorActionsEnum.UNASSIGN_CHAT_MEMBERS) && (
          <LoanActionRows
            component={LoanActionCriteriaRow}
            isDisabled={isLoading}
            isEditAllowed={isEditAllowed}
            itemType={itemType}
            items={action.chatCriteria}
            label="Chat Type"
            optionValueGetter={getOptionValue}
            options={options.chatTypeOptions}
            rootPath={`actions[${actionIndex}].chatCriteria`}
          />
        )}

        {itemType === LoanEditorActionsEnum.ASSIGN_TASK && (
          <LoanActionRows
            component={LoanActionTaskRow}
            isDisabled={isLoading}
            isEditAllowed={isEditAllowed}
            itemType={itemType}
            items={action.assignTask}
            label="Task Type"
            optionValueGetter={getOptionValue}
            options={options.taskTypesOptions}
            rootPath={`actions[${actionIndex}].assignTask`}
          />
        )}

        {itemType === LoanEditorActionsEnum.SEND_EMAIL_TEMPLATE && (
          <LoanActionRows
            component={LoanActionEmailRow}
            isDisabled={isLoading}
            isEditAllowed={isEditAllowed}
            itemType={itemType}
            items={action.sendEmailTemplate}
            optionValueGetter={getOptionValue}
            options={options.emailTemplatesOptions}
            rootPath={`actions[${actionIndex}].sendEmailTemplate`}
          />
        )}

        {itemType === LoanEditorActionsEnum.SUBMIT_UCD && (
          <LoanActionRows
            component={LoanActionSubmitUcdRow}
            isDisabled={isLoading}
            isEditAllowed={isEditAllowed}
            itemType={itemType}
            items={action.submitUcd}
            optionValueGetter={getOptionValue}
            options={ucdTypeOptions}
            rootPath={`actions[${actionIndex}].submitUcd`}
          />
        )}

        {itemType === LoanEditorActionsEnum.RUN_LOAN_CALCULATIONS && (
          <LoanActionRows
            component={LoanActionRunLoanCalculationsRow}
            isDisabled={isLoading}
            isEditAllowed={isEditAllowed}
            itemType={itemType}
            items={action.runLoanCalculations}
            optionValueGetter={getOptionValue}
            options={runLoanCalculationsSaveModeOptions}
            rootPath={`actions[${actionIndex}].runLoanCalculations`}
          />
        )}

        {itemType === LoanEditorActionsEnum.CREATE_AND_DOWNLOAD_MISMO_FILE && (
          <LoanActionRows
            component={LoanActionMismoDocumentRow}
            isDisabled={isLoading}
            isEditAllowed={isEditAllowed}
            itemType={itemType}
            items={action.mismoDocument}
            optionValueGetter={getOptionValue}
            options={mismoDocumentTypeOptions}
            rootPath={`actions[${actionIndex}].mismoDocument`}
          />
        )}

        {itemType === LoanEditorActionsEnum.EXPORT_LOAN_PACKAGE && (
          <LoanActionRows
            component={LoanActionExportLoanPackageRow}
            isDisabled={isLoading}
            isEditAllowed={isEditAllowed}
            itemType={itemType}
            items={action.exportLoanPackage}
            optionValueGetter={getOptionValue}
            options={emptyOptions}
            rootPath={`actions[${actionIndex}].exportLoanPackage`}
          />
        )}

        {itemType === LoanEditorActionsEnum.ADD_CONDITION && (
          <LoanActionRows
            component={LoanActionConditionRow}
            isDisabled={isLoading}
            isEditAllowed={isEditAllowed}
            itemType={itemType}
            items={action.conditions}
            optionValueGetter={getOptionValue}
            options={options.conditionsOptions}
            rootPath={`actions[${actionIndex}].conditions`}
          />
        )}

        {itemType === LoanEditorActionsEnum.GRANT_CLOSING_PORTAL_ACCESS && (
          <LoanActionRows
            component={LoanActionClosingPortalAccessRow}
            isAutomatedActions={isAutomatedActions}
            isDisabled={isLoading}
            isEditAllowed={isEditAllowed}
            items={action.grantClosingPortalAccess}
            optionValueGetter={getOptionValue}
            options={emptyOptions}
            rootPath={`actions[${actionIndex}].grantClosingPortalAccess`}
          />
        )}

        {itemType === LoanEditorActionsEnum.SAVE_PENDING_CHANGES && (
          <>
            <LoanActionRows
              allowEmpty
              component={LoanActionSavePendingChangesPermissionRow}
              isAutomatedActions={isAutomatedActions}
              isDisabled={isLoading}
              isEditAllowed={isEditAllowed}
              items={action.permissions}
              optionValueGetter={getOptionValue}
              options={emptyOptions}
              rootPath={`actions[${actionIndex}].permissions`}
            />
            {action.permissions.length === 0 && (
              <FormHelperText>
                No permissions added — every user who can see this action can cancel and save. Add a
                permission to restrict who can.
              </FormHelperText>
            )}
          </>
        )}

        {itemType === LoanEditorActionsEnum.SET_APPLICATION_IMPOUND && (
          <LoanActionRows
            component={LoanActionImpoundRow}
            impoundTypeOptions={options.impoundTypeOptions}
            isDisabled={isLoading}
            isEditAllowed={isEditAllowed}
            items={action.setApplicationImpound}
            optionValueGetter={getOptionValue}
            options={emptyOptions}
            rootPath={`actions[${actionIndex}].setApplicationImpound`}
          />
        )}

        {itemType === LoanEditorActionsEnum.RUN_FHA_SERVICE && (
          <LoanActionRows
            component={LoanActionFhaServiceRow}
            isDisabled={isLoading}
            isEditAllowed={isEditAllowed}
            items={action.runFhaService}
            optionValueGetter={getOptionValue}
            options={emptyOptions}
            rootPath={`actions[${actionIndex}].runFhaService`}
          />
        )}

        {itemType === LoanEditorActionsEnum.RUN_VA_SERVICE && (
          <LoanActionRows
            component={LoanActionVaServiceRow}
            isDisabled={isLoading}
            isEditAllowed={isEditAllowed}
            items={action.runVaService}
            optionValueGetter={getOptionValue}
            options={emptyOptions}
            rootPath={`actions[${actionIndex}].runVaService`}
          />
        )}

        {itemType === LoanEditorActionsEnum.RUN_APPRAISAL_SERVICE && (
          <LoanActionRows
            component={LoanActionAppraisalServiceRow}
            documentTypesOptions={options.documentTypesOptions}
            isAutomatedActions={isAutomatedActions}
            isDisabled={isLoading}
            isEditAllowed={isEditAllowed}
            items={action.runAppraisalService}
            optionValueGetter={getOptionValue}
            options={emptyOptions}
            rootPath={`actions[${actionIndex}].runAppraisalService`}
            taskTypesOptions={options.taskTypesOptions}
          />
        )}

        {itemType === LoanEditorActionsEnum.RUN_APPRAISAL_SERVICE_QC && (
          <LoanActionRows
            component={LoanActionAppraisalQcServiceRow}
            documentTypesOptions={options.documentTypesOptions}
            isAutomatedActions={isAutomatedActions}
            isDisabled={isLoading}
            isEditAllowed={isEditAllowed}
            items={action.runAppraisalServiceQc}
            optionValueGetter={getOptionValue}
            options={emptyOptions}
            rootPath={`actions[${actionIndex}].runAppraisalServiceQc`}
          />
        )}

        {itemType === LoanEditorActionsEnum.RUN_FLOOD_SERVICE && (
          <LoanActionRows
            component={LoanActionFloodServiceRow}
            floodProductOptions={options.floodProductOptions}
            isAutomatedActions={isAutomatedActions}
            isDisabled={isLoading}
            isEditAllowed={isEditAllowed}
            items={action.runFloodService}
            optionValueGetter={getOptionValue}
            options={emptyOptions}
            rootPath={`actions[${actionIndex}].runFloodService`}
            taskTypesOptions={options.taskTypesOptions}
          />
        )}

        {itemType === LoanEditorActionsEnum.UPDATE_FLOOD_DETERMINATION && (
          <LoanActionRows
            component={LoanActionUpdateFloodDeterminationRow}
            isDisabled={isLoading}
            isEditAllowed={isEditAllowed}
            items={action.updateFloodDetermination}
            optionValueGetter={getOptionValue}
            options={emptyOptions}
            rootPath={`actions[${actionIndex}].updateFloodDetermination`}
          />
        )}

        {mersRowConfig && (
          <LoanActionRows
            component={LoanActionMersRow}
            isDisabled={isLoading}
            isEditAllowed={isEditAllowed}
            items={action[mersRowConfig.formKey]}
            kindLabel={mersRowConfig.kindLabel}
            kindOptions={mersRowConfig.kindOptions}
            optionValueGetter={getOptionValue}
            options={emptyOptions}
            rootPath={`actions[${actionIndex}].${mersRowConfig.formKey}`}
            taskTypesOptions={options.taskTypesOptions}
            toggle={mersRowConfig.toggle}
          />
        )}

        {itemType === LoanEditorActionsEnum.PREVIEW_MERS && (
          <LoanActionRows
            component={LoanActionMersPreviewRow}
            isDisabled={isLoading}
            isEditAllowed={isEditAllowed}
            items={action.previewMers}
            optionValueGetter={getOptionValue}
            options={emptyOptions}
            rootPath={`actions[${actionIndex}].previewMers`}
          />
        )}

        {itemType === LoanEditorActionsEnum.REQUEST_ARGYLE_VERIFICATION && (
          <LoanActionRows
            component={LoanActionDuplicatePolicyRow}
            duplicatePolicyOptions={argyleDuplicatePolicyOptions}
            isDisabled={isLoading}
            isEditAllowed={isEditAllowed}
            items={action.requestArgyleVerification}
            optionValueGetter={getOptionValue}
            options={emptyOptions}
            rootPath={`actions[${actionIndex}].requestArgyleVerification`}
          />
        )}

        {itemType === LoanEditorActionsEnum.REQUEST_FINICITY_VERIFICATION && (
          <LoanActionRows
            component={LoanActionDuplicatePolicyRow}
            duplicatePolicyOptions={finicityDuplicatePolicyOptions}
            isDisabled={isLoading}
            isEditAllowed={isEditAllowed}
            items={action.requestFinicityVerification}
            optionValueGetter={getOptionValue}
            options={emptyOptions}
            rootPath={`actions[${actionIndex}].requestFinicityVerification`}
          />
        )}

        {itemType === LoanEditorActionsEnum.ADD_HALCYON_TRANSCRIPT_FORMS && (
          <LoanActionRows
            component={LoanActionHalcyonAddFormsRow}
            isAutomatedActions={isAutomatedActions}
            isDisabled={isLoading}
            isEditAllowed={isEditAllowed}
            items={action.halcyonAddForms}
            optionValueGetter={getOptionValue}
            options={emptyOptions}
            rootPath={`actions[${actionIndex}].halcyonAddForms`}
          />
        )}

        {itemType === LoanEditorActionsEnum.CALL_INTEGRATION && (
          <LoanActionIntegrationRow
            isDisabled={isLoading}
            isEditAllowed={isEditAllowed}
            rootPath={`actions[${actionIndex}].callIntegration`}
          />
        )}

        {_.has(snapdocsFieldsByActionType, itemType) && (
          <LoanActionSnapdocsRow
            documentTypesOptions={options.documentTypesOptions}
            isAutomatedActions={isAutomatedActions}
            isDisabled={isLoading}
            isEditAllowed={isEditAllowed}
            itemType={itemType}
            rootPath={`actions[${actionIndex}].snapdocsClosing`}
          />
        )}

        {mortgageInsuranceLoanActionTypes.includes(itemType) && (
          <LoanActionMortgageInsuranceRow
            isDisabled={isLoading}
            isEditAllowed={isEditAllowed}
            itemType={itemType}
            rootPath={`actions[${actionIndex}].mortgageInsurance`}
          />
        )}

        {itemType === LoanEditorActionsEnum.ASSIGN_SUB_COMPANY_AE && (
          <LoanActionRows
            component={LoanActionAssignAeRow}
            isDisabled={isLoading}
            isEditAllowed={isEditAllowed}
            items={action.assignSubCompanyAe}
            optionValueGetter={getOptionValue}
            options={emptyOptions}
            rootPath={`actions[${actionIndex}].assignSubCompanyAe`}
          />
        )}

        {itemType === LoanEditorActionsEnum.DECIDE_CERTIFICATION && (
          <LoanActionRows
            component={LoanActionDecideCertificationRow}
            isDisabled={isLoading}
            isEditAllowed={isEditAllowed}
            items={action.decideCertification}
            optionValueGetter={getOptionValue}
            options={emptyOptions}
            rootPath={`actions[${actionIndex}].decideCertification`}
          />
        )}

        {itemType === LoanEditorActionsEnum.EXECUTE_AI_PROCESS && (
          <LoanActionRows
            component={LoanActionExecuteAiProcessRow}
            isDisabled={isLoading}
            isEditAllowed={isEditAllowed}
            items={action.executeAiProcess}
            optionValueGetter={getOptionValue}
            options={emptyOptions}
            rootPath={`actions[${actionIndex}].executeAiProcess`}
          />
        )}

        {itemType === LoanEditorActionsEnum.RUN_AI_PROCESS && (
          <LoanActionRunAiProcessRow
            actionIndex={actionIndex}
            isAutomatedActions={isAutomatedActions}
            isDisabled={isLoading}
            isEditAllowed={isEditAllowed}
            rootPath={`actions[${actionIndex}].runAiProcess`}
            tagsOptions={options.tagsOptions}
          />
        )}

        {itemType === LoanEditorActionsEnum.SEND_AI_PROCESS_RESULTS && (
          <LoanActionSendAiProcessResultsRow
            actionIndex={actionIndex}
            isDisabled={isLoading}
            isEditAllowed={isEditAllowed}
            rootPath={`actions[${actionIndex}].sendAiProcessResults`}
            tagsOptions={options.tagsOptions}
          />
        )}
      </LoanActionFormItem>
    );
  });
};

LoanActionsGroup.propTypes = {
  isLoading: PropTypes.bool.isRequired,
  isEditAllowed: PropTypes.bool.isRequired,
  groupName: PropTypes.string,
  isAutomatedActions: PropTypes.bool,
};

export default memo(LoanActionsGroup);
